// ProcessTimeline.js - New Zealand
import React from 'react';
import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';

const ProcessTimeline = () => {
  const timelines = [
    {
      pathway: 'Skilled Migrant Category',
      stages: [
        { step: 'Submit Expression of Interest (EOI)', duration: '1-2 weeks', description: 'Complete online EOI with all details and supporting evidence' },
        { step: 'EOI Selection from Pool', duration: 'Varies', description: 'Wait for selection based on points score (typically 2-4 weeks between selections)' },
        { step: 'Invitation to Apply (ITA)', duration: '4 months', description: 'Receive ITA and gather all required documents within 4 months' },
        { step: 'Lodge Residence Application', duration: 'Within 4 months', description: 'Submit complete application with all supporting documents' },
        { step: 'Application Processing', duration: '6-12 months', description: 'Immigration NZ processes application including verification' },
        { step: 'Residence Visa Grant', duration: '1-2 weeks', description: 'Receive residence visa and make initial entry to NZ' }
      ],
      totalTime: '8-18 months',
      color: 'from-blue-500 to-blue-600'
    },
    {
      pathway: 'Work to Residence',
      stages: [
        { step: 'Secure Job with Accredited Employer', duration: 'Varies', description: 'Obtain job offer from Immigration NZ accredited employer' },
        { step: 'Work Visa Application', duration: '2-4 months', description: 'Apply for work visa (30 months duration)' },
        { step: 'Work in New Zealand', duration: '24 months', description: 'Work for accredited employer for minimum 24 months' },
        { step: 'Residence Application', duration: '1-2 months', description: 'Apply for residence after meeting work requirements' },
        { step: 'Application Processing', duration: '6-9 months', description: 'Immigration NZ processes residence application' },
        { step: 'Residence Visa Grant', duration: '1-2 weeks', description: 'Receive residence visa approval' }
      ],
      totalTime: '30-40 months',
      color: 'from-green-500 to-green-600'
    },
    {
      pathway: 'Partnership Residence',
      stages: [
        { step: 'Gather Relationship Evidence', duration: '2-4 weeks', description: 'Compile evidence of genuine and stable relationship' },
        { step: 'Lodge Application', duration: '1-2 weeks', description: 'Submit partnership residence application with all documents' },
        { step: 'Initial Assessment', duration: '3-6 months', description: 'Immigration NZ reviews application and may request additional info' },
        { step: 'Interview (if required)', duration: 'Varies', description: 'Attend interview if requested by Immigration NZ' },
        { step: 'Application Processing', duration: '12-18 months', description: 'Full processing including verification checks' },
        { step: 'Residence Visa Grant', duration: '1-2 weeks', description: 'Receive residence visa (initially temporary for 2 years)' }
      ],
      totalTime: '12-24 months',
      color: 'from-purple-500 to-purple-600'
    }
  ];

  return (
    <section className="py-16 bg-[#F5F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Standard Processing Timelines
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Understanding the typical timeline for each pathway helps you plan your immigration journey effectively.
          </p>
        </motion.div>

        <div className="space-y-12">
          {timelines.map((timeline, tIndex) => (
            <motion.div
              key={tIndex}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: tIndex * 0.2 }}
              className="bg-white rounded-lg shadow-sm p-6 md:p-8"
            >
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-xl font-semibold text-[#202356] mb-2">
                    {timeline.pathway}
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock size={16} />
                    <span className="font-light">Total Processing Time: <strong>{timeline.totalTime}</strong></span>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                <div className="space-y-8">
                  {timeline.stages.map((stage, sIndex) => (
                    <motion.div
                      key={sIndex}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: sIndex * 0.1 }}
                      className="relative pl-12"
                    >
                      <div className={`absolute left-0 w-8 h-8 rounded-full bg-gradient-to-br ${timeline.color} flex items-center justify-center text-white text-sm font-semibold`}>
                        {sIndex + 1}
                      </div>
                      <div className="bg-[#F5F8FA] p-4 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="text-base font-semibold text-[#202356]">
                            {stage.step}
                          </h4>
                          <span className="text-xs font-light text-[#2542D4] bg-white px-3 py-1 rounded-full whitespace-nowrap ml-4">
                            {stage.duration}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm font-light leading-relaxed">
                          {stage.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 grid md:grid-cols-2 gap-6"
        >
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h4 className="text-base font-semibold text-[#202356] mb-3">
              Processing Time Factors
            </h4>
            <ul className="space-y-2 text-sm text-gray-700 font-light">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Completeness and accuracy of application</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Points score and occupation demand</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Immigration NZ workload and priorities</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Additional verification requirements</span>
              </li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h4 className="text-base font-semibold text-[#202356] mb-3">
              How We Help
            </h4>
            <ul className="space-y-2 text-sm text-gray-700 font-light">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Maximize your points score for SMC</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Ensure complete and accurate documentation</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Monitor EOI selections and application status</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Respond promptly to Immigration NZ requests</span>
              </li>
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessTimeline;
